/* eslint-disable indent */
import checkSvg from '@src/main/assets/icons/check-red.svg';
import { Category, Type } from '@src/main/modules/parameters/domain/issues/category.interface';
import TypeForm from '@src/main/modules/parameters/ui/issues/components/TypeForm';
import { useCreateType } from '@src/main/modules/parameters/ui/issues/hooks/create/useCreateType';
import { useDeleteType } from '@src/main/modules/parameters/ui/issues/hooks/delete/useDeleteType';
import { useCategory } from '@src/main/modules/parameters/ui/issues/hooks/find/useCategory';
import { useUpdateType } from '@src/main/modules/parameters/ui/issues/hooks/update/useUpdateType';
import { getTypeSchema } from '@src/main/modules/parameters/ui/issues/typeSchema';
import { useToast } from '@src/main/shared/context/ToastContext';
import AppDialog from '@src/main/shared/ui/components/dialog/AppDialog';
import ConfirmDeleteDialog from '@src/main/shared/ui/components/dialog/ConfirmDeleteDialog';
import HeaderTitle from '@src/main/shared/ui/components/header/HeaderTitle';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import AppTableSingle from '@src/main/shared/ui/components/table/AppTableSingle';
import TableHeader from '@src/main/shared/ui/components/table/TableHeader';
import { showErrorMessage, showSuccessMessage } from '@src/main/shared/utils/toastUtils';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

interface TableRow extends Type {
  id: string;
}

interface TypesProps {
  categoryId: string;
  selectedCategory?: Category;
  onSelectType: (type: Type | null) => void;
  selectedType: Type | null;
}

const Types: FC<TypesProps> = ({ categoryId, selectedCategory, onSelectType, selectedType }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: category, isLoading: loadingCategory } = useCategory(categoryId);

  const { mutateAsync: createType } = useCreateType();
  const { mutateAsync: updateType } = useUpdateType();
  const { mutateAsync: deleteType } = useDeleteType();

  const [typeToDelete, setTypeToDelete] = useState<Type | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingType, setEditingType] = useState(false);
  const [newEditType, setNewEditType] = useState<Partial<Type>>({
    title: '',
    visibleOnApp: true,
    categories: selectedCategory ? [selectedCategory] : [],
    subtypes: [],
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);

  const types = category?.types ?? [];
  const typesWithId: TableRow[] = types.map((t) => ({
    id: t.typeId!,
    ...t,
    visibleOnAppDisplay: t.visibleOnApp ? (
      <img src={checkSvg} alt="Visible" width={16} height={16} />
    ) : null,
  }));

  const handleShowCreateModal = () => {
    setNewEditType({
      title: '',
      visibleOnApp: false,
      categories: selectedCategory ? [selectedCategory] : [],
      subtypes: [],
    });
    setEditingType(false);
    setValidationErrors({});
    setIsModalVisible(true);
  };

  const handleEditType = (row: TableRow) => {
    setNewEditType({
      typeId: row.typeId,
      title: row.title,
      visibleOnApp: row.visibleOnApp,
      categories: row.categories,
      subtypes: row.subtypes,
    });
    setEditingType(true);
    setValidationErrors({});
    setIsModalVisible(true);
  };

  const handleCreateEdit = async () => {
    if (!category?.categoryId) return;

    const schema = getTypeSchema(t);

    try {
      schema.parse(newEditType);

      if (editingType && newEditType.typeId) {
        await updateType({
          id: newEditType.typeId!,
          data: {
            typeId: newEditType.typeId!,
            title: newEditType.title!,
            visibleOnApp: newEditType.visibleOnApp ?? false,
            category: category.categoryId,
          },
        });
        showSuccessMessage(toast, t('parameters.issues.types.updatedSuccess'));
      } else {
        await createType({
          title: newEditType.title!,
          visibleOnApp: newEditType.visibleOnApp ?? false,
          category: category.categoryId,
        });
        showSuccessMessage(toast, t('parameters.issues.types.createdSuccess'));
      }

      await queryClient.invalidateQueries({ queryKey: ['category', categoryId] });
      setIsModalVisible(false);
      setValidationErrors({});
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            errors[err.path[0] as string] = err.message;
          }
        });
        setValidationErrors(errors);
      } else {
        showErrorMessage(toast, error);
      }
    }
  };

  const confirmDelete = async () => {
    if (!typeToDelete) return;
    try {
      await deleteType(typeToDelete.typeId!);
      if (selectedType?.typeId === typeToDelete.typeId) {
        onSelectType(null);
      }
      await queryClient.invalidateQueries({ queryKey: ['category', categoryId] });
      showSuccessMessage(toast, t('parameters.issues.types.deletedSuccess'));
    } catch (error) {
      showErrorMessage(toast, error);
    } finally {
      setTypeToDelete(null);
    }
  };

  return (
    <>
      <div>
        <HeaderTitle title={t('parameters.issues.types.title')} />

        <TableHeader
          onCreate={handleShowCreateModal}
          onDelete={() => setTypeToDelete(selectedType || null)}
          onRefresh={async () => {
            setIsRefreshing(true);
            await queryClient.invalidateQueries({ queryKey: ['category', categoryId] });
            setIsRefreshing(false);
          }}
          visibility={[true, true, false, true, false, false]}
        />

        <AppLoading isLoading={loadingCategory || isRefreshing}>
          <AppTableSingle
            headers={[
              { name: t('parameters.issues.titleField'), field: 'title' },
              { name: t('parameters.issues.visibleOnApp'), field: 'visibleOnAppDisplay' },
            ]}
            data={typesWithId}
            onEdit={handleEditType}
            onDelete={(row) => setTypeToDelete({ ...row })}
            onSelectedChange={(row: TableRow | null) =>
              onSelectType(row ? (types.find((t) => t.typeId === row.id) ?? null) : null)
            }
            selectedRow={selectedType ? { id: selectedType.typeId!, ...selectedType } : null}
          />
        </AppLoading>
      </div>

      <AppDialog
        visible={isModalVisible}
        title={
          editingType
            ? t('parameters.issues.types.updateType', { type: newEditType.title })
            : t('parameters.issues.types.createType', {
                category: selectedCategory?.title,
              })
        }
        onClose={() => {
          setIsModalVisible(false);
          setValidationErrors({});
        }}
        disableButtons={loadingCategory}
        onConfirm={handleCreateEdit}
      >
        <TypeForm
          values={newEditType}
          handleChange={(field, value) => setNewEditType((prev) => ({ ...prev, [field]: value }))}
          errors={validationErrors}
        />
      </AppDialog>

      <ConfirmDeleteDialog
        visible={!!typeToDelete}
        confirmDelete={confirmDelete}
        rejectDelete={() => setTypeToDelete(null)}
        message={t('parameters.issues.types.confirmDelete', { type: typeToDelete?.title })}
      />
    </>
  );
};

export default Types;
